import React, { useEffect, useMemo } from 'react';
import { IOverrideFilterInfo } from '../../../../redux/Calculations/CurrentCalculationSessionInterface';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux';
import { usePickListOptions } from '../../../../hooks/usePickListOptions';
import { updateFilterPanel } from '../../../../redux/Calculations/CalculationsActions';
import { Checkbox, Skeleton, Stack } from '@mui/material';
import { fontSize, palette } from '../../../..';
import { getSQLDataType, toBoolean } from '../../../CommonFunctions/CommonFunctions';
import { IFlow } from '../../Conditions/Evaluation/EvaluationInterface';
interface IPickList {
  propKey: string;
  filterInfo: IOverrideFilterInfo;
  calculationUid: string | null;
  ensureNodeVisible?: () => void;
}
function PickList({ propKey, filterInfo, calculationUid, ensureNodeVisible }: IPickList) {
  const dispatch = useDispatch();
  const { dsInfo, savedCalculations } = useSelector((state: RootState) => ({
    dsInfo: state.chartProperties.properties[propKey]?.selectedDs as {
      connectionId: string;
      id: string;
      datasetName: string;
      isFlatFileDataset: boolean;
      workSpaceId: string;
    },
    // tableId: state.chartProperties.properties[propKey]?.selectedTableId,
    savedCalculations: state.calculations.savedCalculations,
  }));
  const calcDetails = useMemo(() => {
    if (!calculationUid) return null;
    const calc = savedCalculations.find((calc) => calc.uuid === calculationUid);
    if (!calc) return null;
    
    return {
      calculatedFieldName: calc.calculationInfo.calculatedFieldName || '',
      flows: calc.calculationInfo.flows,
      fields: calc.calculationInfo.fields,
      conditionFilters: calc.calculationInfo.conditionFilters,
    } as IFlow;
  }, [calculationUid, savedCalculations]);
  const {
    result: options,
    error,
    loading,
  } = usePickListOptions(
    dsInfo.connectionId,
    dsInfo.id,
    dsInfo.workSpaceId,
    calcDetails,
    filterInfo.timeGrain,
    calcDetails ? Object.values(calcDetails.fields)[0].tableId : filterInfo.tableId,
    calcDetails ? calcDetails.calculatedFieldName : filterInfo.fieldName,
    filterInfo.dataType,
    !!calcDetails,
  );
  useEffect(() => {
    if (loading || error) return;
    const validSelections = filterInfo.userSelection.filter((selected) =>
      options.includes(selected),
    );
    const shouldDispatch =
      filterInfo.userSelection.length === 0 ||
      validSelections.length !== filterInfo.userSelection.length;
    if (shouldDispatch) {
      dispatch(
        updateFilterPanel(propKey, 'update', filterInfo.filterId, {
          ...filterInfo,
          userSelection: [],
          isAllSelected: false,
        }),
      );
    }
  }, [options]);
  const getOption = (value: string | number | null) => {
    if (value === null) return '(Blank)';
    if (value === '') return '(Empty)';
    return String(value);
  };
  const handleCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>, type?: string) => {
    const option = e.target.name.toString();
    const isChecked = e.target.checked;
    if (option === '(All)') {
      if (isChecked) {
        dispatch(
          updateFilterPanel(propKey, 'update', filterInfo.filterId, {
            ...filterInfo,
            userSelection: [],
            isAllSelected: true,
          }),
        );
      } else {
        dispatch(
          updateFilterPanel(propKey, 'update', filterInfo.filterId, {
            ...filterInfo,
            userSelection: [],
            isAllSelected: false,
          }),
        );
      }
    } else {
      if (isChecked) {
        let _name: string | number | null;
        if (option === '(Blank)') {
          _name = null;
        } else if (option === '(Empty)') {
          _name = '';
        } else {
          const parsedNumber = Number(option);
          _name = isNaN(parsedNumber) ? option : parsedNumber;
        }
        const newSelection = [
          ...filterInfo.userSelection,
          type === 'boolean' ? toBoolean(_name as string) : _name,
        ];
        dispatch(
          updateFilterPanel(propKey, 'update', filterInfo.filterId, {
            ...filterInfo,
            userSelection: newSelection.length === options.length ? [] : newSelection,
            isAllSelected: newSelection.length === options.length,
          }),
        );
      } else {
        const currentSelection = filterInfo.isAllSelected ? options : filterInfo.userSelection;
        const newSelection = currentSelection.filter((_option) => {
          const targetName =
            option === '(Blank)'
              ? null
              : option === '(Empty)'
                ? ''
                : !isNaN(Number(option))
                  ? Number(option)
                  : type === 'boolean'
                    ? toBoolean(option)
                    : option;
          return _option !== targetName;
        });
        dispatch(
          updateFilterPanel(propKey, 'update', filterInfo.filterId, {
            ...filterInfo,
            userSelection: newSelection.length === options.length ? [] : newSelection,
            isAllSelected: newSelection.length === options.length,
          }),
        );
      }
    }
  };

  useEffect(() => {
    if (loading || error) return;
    if (!options || options.length === 0) return;

    requestAnimationFrame(() => {
     ensureNodeVisible && ensureNodeVisible();
    });
  }, [loading, options]);


  if (loading) {
    return (
      <Stack
        spacing={1}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
        <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
        <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
      </Stack>
    );
  }
  if (error) {
    return (
      <div
        style={{
          color: '#850000ff',
        }}
      >
        {' '}
        {error.code === 422 ? 'The Column is no longer available' : 'Error: ' + error.message}
      </div>
    );
  }
  return (
    <div>
      <label
        style={{
          marginLeft: '2.7px',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          width: '90%',
          height: '1rem',
        }}
      >
        <Checkbox
          // disabled={disabled}
          checked={filterInfo.isAllSelected}
          sx={{
            padding: '0',
            transform: 'scale(0.8)',
            // marginLeft: "10px",
            paddingRight: '0px',
            '&.Mui-checked': {
              color: filterInfo.shouldExclude ? 'orange' : '#a6a6a6',
            },
          }}
          onChange={(e) => handleCheckboxClick(e)}
          name="(All)"
        />

        <span
          title="(All)"
          style={{
            marginLeft: '0.3rem',
            // marginTop: "3.5px",
            justifySelf: 'center',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            fontSize: fontSize.medium,
            color: palette.primary.contrastText,
            textDecoration:
              filterInfo.shouldExclude && filterInfo.isAllSelected ? 'line-through' : 'none',
          }}
        >
          {'(All)'}
        </span>
      </label>
      {options.map((option, index) => (
        <label
          key={index}
          style={{
            marginLeft: '2.5px',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            width: '90%',
            height: '1rem',
          }}
        >
          <Checkbox
            // disabled={disabled}
            checked={filterInfo.isAllSelected || filterInfo.userSelection.includes(option)}
            sx={{
              padding: '0',
              transform: 'scale(0.8)',
              // marginLeft: "10px",
              paddingRight: '0px',
              '&.Mui-checked': {
                color: filterInfo.shouldExclude ? 'orange' : '#a6a6a6',
              },
            }}
            onChange={(e) => {
              handleCheckboxClick(e, getSQLDataType(option));
            }}
            name={getOption(option)}
          />

          <span
            title={getOption(option)}
            style={{
              marginLeft: '0.3rem',
              // marginTop: "3.5px",
              justifySelf: 'center',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              fontSize: fontSize.medium,
              color: palette.primary.contrastText,
              textTransform: 'capitalize',
              textDecoration:
                filterInfo.shouldExclude &&
                (filterInfo.isAllSelected || filterInfo.userSelection.includes(option))
                  ? 'line-through'
                  : 'none',
            }}
          >
            {getOption(option)}
          </span>
        </label>
      ))}
    </div>
  );
}

export default PickList;
